import React, { useState, useEffect, useReducer } from "react";
import PropTypes from "prop-types";
import {
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  IconButton,
  Stack,
  Box,
  Container,
  Skeleton,
  TableHead,
  TablePagination,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Link, useParams } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import SplitterLayout from "react-splitter-layout";
import "react-splitter-layout/lib/index.css";
import TemplateBox from "components/TemplateBox";
import NoData from "components/NoData";
import { fetchAccNumberContacts, fetchResourcePref } from "../../apiService";
import { alarmColumns } from "./columns";
import { resourcePrefColumns } from "./resourcePrefColumns";
import { I18n } from "aws-amplify/utils";
import { useTemplateController } from "context";
import { CallSplit } from "@mui/icons-material";
const initialState = {
  loading: true,
  data: null,
  error: null,
};

function contactAccNumberReducer(state, action) {
  switch (action.type) {
    case "DATA_RECEIVED":
      return { ...state, loading: false, data: action.value, error: null };
    case "ERROR":
      return { ...state, loading: false, error: action.error };
    default:
      return state;
  }
}

function resourcePrefReducer(state, action) {
  switch (action.type) {
    case "DATA_RECEIVED":
      return { ...state, loading: false, data: action.value, error: null };
    case "ERROR":
      return { ...state, loading: false, error: action.error };
    default:
      return state;
  }
}

function Details({ isOpen, onClose, rowData, loading, language }) {
  const { id } = useParams();
  const theme = useTheme();
  const [contactAccNumberState, dispatchContactAccNumber] = useReducer(
    contactAccNumberReducer,
    initialState
  );
  const [resourcePrefState, dispatchResourcePref] = useReducer(
    resourcePrefReducer,
    initialState
  );
  const mobileDevice = !useMediaQuery(theme.breakpoints.up("md"));
  const isMobileDevice = useMediaQuery("(max-width:600px)");
  const [alarmsSameDevPage, setAlarmsSameDevPage] = useState(0);
  const [rowsPerAlarmsSameDevPage, setRowsPerAlarmsSameDevPage] = useState(5);
  const [controller] = useTemplateController();
  const { darkMode } = controller;
  const [resourcePrefPage, setResourcePrefPage] = useState(0);
  const [rowsPerResourcePrefPage, setRowsPerResourcePrefPage] = useState(5);
  // Estrarre il primo (o unico) elemento dell'array rowData
  const data = rowData && rowData.length > 0 ? rowData[0] : {};

  useEffect(() => {
    if (!loading && rowData && data.account_number) {
      const fetchData = async () => {
        try {
          const response = await fetchAccNumberContacts(data.account_id);
          dispatchContactAccNumber({ type: "DATA_RECEIVED", value: response });
        } catch (error) {
          dispatchContactAccNumber({ type: "ERROR", error });
        }
      };
      fetchData();
    }
  }, [loading, data]);

  useEffect(() => {
    if (!loading && rowData && data.account_number) {
      const fetchData = async () => {
        try {
          const response = await fetchResourcePref(data.account_id);
          dispatchResourcePref({ type: "DATA_RECEIVED", value: response });
        } catch (error) {
          dispatchResourcePref({ type: "ERROR", error });
        }
      };
      fetchData();
    }
  }, [loading, data]);

  const headerCells = [
    {
      id: "name",
      label: I18n.get("Store Name"),
      rendercell: (params) => (
        <Typography
          component="a"
          href={`https://eptarefrigeration.lightning.force.com/${params.row.account_id}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            fontSize: "medium",
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      id: "phone",
      label: I18n.get("Phone"),
      rendercell: (params) => (
        <Typography
          component="a"
          href={`tel:${params.value}`}
          sx={{
            fontSize: "medium",
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      id: "email",
      label: I18n.get("Email"),
      rendercell: (params) => (
        <Typography
          component="a"
          href={`mailto:${params.value}`}
          sx={{
            fontSize: "medium",
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          {params.value}
        </Typography>
      ),
    },

    { id: "billing_country", label: I18n.get("Country") },
    { id: "billing_city", label: I18n.get("City") },
    { id: "billing_street", label: I18n.get("Street") },
    { id: "service_territory_name", label: I18n.get("Service Territory") },
  ];

  const [selectedRowData, setSelectedRowData] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  const [selectedAuditId, setSelectedAuditId] = useState(null);

  const handlePopUpButtonClick = (id) => {
    setSelectedAuditId(id);
    setShowPopup(!showPopup);
  };
  

  const renderTableBody = () => {
    if (!loading && (!data || Object.keys(data).length === 0)) {
      return (
        <Card>
          <CardContent
            sx={{ padding: "1rem", "&:last-child": { padding: "1rem" } }}
          >
            <Stack
              spacing={2}
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              sx={{ pb: 2 }}
            >
              <Typography variant="h5">
                {I18n.get("Store Information")}
              </Typography>
            </Stack>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <NoData />
            </Box>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardContent
          sx={{ padding: "1rem", "&:last-child": { padding: "1rem" } }}
        >
          <Stack
            spacing={2}
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            sx={{ pb: 2 }}
          >
            <Typography variant="h5">
              {I18n.get("Store Information")}
            </Typography>
          </Stack>
          <TableContainer>
            <Table>
              <TableBody>
                {headerCells.map((column, colIndex) => (
                  <React.Fragment key={colIndex}>
                    <TableCell
                      style={{
                        whiteSpace: "normal",
                        wordWrap: "break-word",
                        padding: "0px",
                        borderBottom: "1px solid #e0e0e0",
                        fontSize: "14px",
                        lineHeight: 1.6,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: "bold",
                          fontSize: "15px",
                        }}
                      >
                        {column.label}:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "14px",
                        }}
                      >
                        {loading ? (
                          <Skeleton />
                        ) : column.rendercell ? (
                          column.rendercell({
                            value: data[column.id],
                            row: data,
                          })
                        ) : data[column.id] !== undefined &&
                          data[column.id] !== null ? (
                          data[column.id]
                        ) : (
                          ""
                        )}
                      </Typography>
                    </TableCell>

                    {(colIndex + 1) % (isMobileDevice ? 1 : 1) === 0 && (
                      <TableRow key={`spacer-${colIndex}`}></TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };
  function getBrowserName() {
    const userAgent = navigator.userAgent;
    if (userAgent.includes("Firefox")) {
      return "Firefox";
    } else if (userAgent.includes("Edg")) {
      return "Edge";
    } else if (userAgent.includes("Chrome")) {
      return "Chrome";
    }
  }
  const browser = getBrowserName();
  const renderNotesCard = () => {
    return (
      <Card sx={{ width: "100%" }}>
        <CardContent
          sx={{ padding: "1rem", "&:last-child": { padding: "1rem" } }}
        >
          <Stack
            spacing={2}
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            sx={{ pb: 2 }}
          >
            <Typography variant="h5">{I18n.get("Notes")}</Typography>
          </Stack>
          <TableContainer sx={{ width: "100%" }}>
            <Table sx={{ width: "100%" }}>
              <TableBody>
                <TableRow>
                  <TableCell
                    style={{
                      whiteSpace: "normal",
                      wordWrap: "break-word",
                      overflowWrap: "break-word",
                      wordBreak: "break-all", // Ensure text breaks even for long words
                      maxWidth: "100%", // Set max width to avoid overflow
                      padding: "12px  12px  0px 0px",
                      fontSize: "14px",
                      lineHeight: 1.6,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: "bold",
                        fontSize: "15px",
                        display: "block",
                      }}
                    >
                      {I18n.get("Contact Notes:")}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "14px",
                        display: "block",
                        whiteSpace: "normal",
                        wordBreak: "break-all", // Break long unbroken strings
                      }}
                    >
                      {loading ? <Skeleton /> : data.secondary_note || ""}
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    style={{
                      whiteSpace: "normal",
                      wordWrap: "break-word",
                      overflowWrap: "break-word",
                      wordBreak: "break-all", // Ensure text breaks even for long words
                      maxWidth: "100%", // Set max width to avoid overflow
                      padding: "0px",
                      fontSize: "14px",
                      lineHeight: 1.6,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: "bold",
                        fontSize: "15px",
                        display: "block",
                      }}
                    >
                      {I18n.get("Suggestions:")}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "14px",
                        display: "block",
                        overflowWrap: "break-word",
                        wordBreak: "break-all", // Break long unbroken strings
                      }}
                    >
                      {loading ? <Skeleton /> : data.suggestions || ""}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };
  function renderContactAccInfo() {
    if (contactAccNumberState.loading) {
      return (
        <Card>
          <CardContent
            sx={{ padding: "1rem", "&:last-child": { padding: "1rem" } }}
          >
            <Skeleton variant="rectangular" width="100%" height={400} />
          </CardContent>
        </Card>
      );
    }

    if (
      !contactAccNumberState.data ||
      contactAccNumberState.data.length === 0
    ) {
      return (
        <Card>
          <CardContent
            sx={{ padding: "1rem", "&:last-child": { padding: "1rem" } }}
          >
            <Stack
              spacing={2}
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              sx={{ pb: 2 }}
            >
              <Typography variant="h5">
                {I18n.get("Telemonitoring Active Contacts")}
              </Typography>
            </Stack>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <NoData />
            </Box>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardContent
          sx={{ padding: "1rem", "&:last-child": { padding: "1rem" } }}
        >
          <Stack
            spacing={2}
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            sx={{ pb: 2 }}
          >
            <Typography variant="h5">
              {I18n.get("Telemonitoring Active Contacts")}
            </Typography>
          </Stack>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {alarmColumns().map((column) => (
                    <TableCell key={column.id}>{column.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {contactAccNumberState.data
                  .slice(
                    alarmsSameDevPage * rowsPerAlarmsSameDevPage,
                    alarmsSameDevPage * rowsPerAlarmsSameDevPage +
                      rowsPerAlarmsSameDevPage
                  )
                  .map((mappingRow, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {alarmColumns().map((column, colIndex) => (
                        <TableCell key={colIndex} sx={{ padding: "0px" }}>
                          {column.rendercell
                            ? column.rendercell({
                                value: mappingRow[column.id],
                                row: mappingRow,
                              })
                            : mappingRow[column.id] !== undefined &&
                              mappingRow[column.id] !== null
                            ? I18n.get(mappingRow[column.id])
                            : ""}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={contactAccNumberState.data.length}
            page={alarmsSameDevPage}
            onPageChange={(event, newPage) => setAlarmsSameDevPage(newPage)}
            rowsPerPage={rowsPerAlarmsSameDevPage}
            onRowsPerPageChange={(event) => {
              setRowsPerAlarmsSameDevPage(parseInt(event.target.value, 10));
              setAlarmsSameDevPage(0);
            }}
            rowsPerPageOptions={[]}
          />
        </CardContent>
      </Card>
    );
  }

  function renderResourcePref() {
    if (resourcePrefState.loading) {
      return (
        <Card>
          <CardContent
            sx={{ padding: "1rem", "&:last-child": { padding: "1rem" } }}
          >
            <Skeleton variant="rectangular" width="100%" height={400} />
          </CardContent>
        </Card>
      );
    }

    if (!resourcePrefState.data || resourcePrefState.data.length === 0) {
      return (
        <Card>
          <CardContent
            sx={{ padding: "1rem", "&:last-child": { padding: "1rem" } }}
          >
            <Stack
              spacing={2}
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              sx={{ pb: 2 }}
            >
              <Typography variant="h5">
                {I18n.get("Resource Preference")}
              </Typography>
            </Stack>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <NoData />
            </Box>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardContent
          sx={{ padding: "1rem", "&:last-child": { padding: "1rem" } }}
        >
          <Stack
            spacing={2}
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            sx={{ pb: 2 }}
          >
            <Typography variant="h5">
              {I18n.get("Resource Preference")}
            </Typography>
          </Stack>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {resourcePrefColumns.map((column) => (
                    <TableCell key={column.id}>{column.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {resourcePrefState.data
                  .slice(
                    resourcePrefPage * rowsPerResourcePrefPage,
                    resourcePrefPage * rowsPerResourcePrefPage +
                      rowsPerResourcePrefPage
                  )
                  .map((mappingRow, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {resourcePrefColumns.map((column, colIndex) => (
                        <TableCell key={colIndex} sx={{ padding: "0px" }}>
                          {column.rendercell
                            ? column.rendercell({
                                value: mappingRow[column.id],
                                row: mappingRow,
                              })
                            : mappingRow[column.id] !== undefined &&
                              mappingRow[column.id] !== null
                            ? mappingRow[column.id]
                            : ""}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={resourcePrefState.data.length}
            page={resourcePrefPage}
            onPageChange={(event, newPage) => setResourcePrefPage(newPage)}
            rowsPerPage={rowsPerResourcePrefPage}
            onRowsPerPageChange={(event) => {
              setRowsPerResourcePrefPage(parseInt(event.target.value, 10));
              setResourcePrefPage(0);
            }}
            rowsPerPageOptions={[]}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <SplitterLayout
        vertical={mobileDevice}
        percentage={true}
        primaryMinSize={mobileDevice ? 30 : 60}
        secondaryMinSize={20}
        secondaryInitialSize={50}
      >
        <div>
          <TemplateBox height="15px" />
          <Box
            sx={{
              position: "sticky",
              top: 0,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              zIndex: 10,
              bgcolor: darkMode ? "#1f2020" : "background.paper",
              p: 2,
              borderRadius: 2, // Adds rounded corners
              boxShadow: 1, // Adds shadow similar to a card
            }}
          >
            <Typography variant="h5" sx={{ fontSize: "1.2rem" }}>
              {`${data.original_store}`}
            </Typography>
            <Box>
              {/* <IconButton
                color="primary"
                onClick={() => handlePopUpButtonClick(id)} //aprire split corretto
              >
                <CallSplit />
              </IconButton> */}
              <IconButton color="secondary" onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <Box
            marginBottom={
              browser === "Firefox"
                ? "83px"
                : browser === "Chrome"
                ? "12px"
                : "0px"
            }
          >
            <TemplateBox mt={3} mb={2}>
              <Grid container spacing={0.5}>
                <Grid item xs={12} md={12} lg={12} mb={3}>
                  <TemplateBox px={2}>{renderNotesCard()}</TemplateBox>
                  <Grid item xs={12} md={12} lg={12} mb={2} mt={2}>
                    <TemplateBox px={2}>{renderTableBody()}</TemplateBox>
                  </Grid>
                  <Grid item xs={12} md={12} lg={12} mb={2}>
                    <TemplateBox px={2}>{renderResourcePref()}</TemplateBox>
                  </Grid>
                  <Grid item xs={12} md={12} lg={12} mb={2}>
                    <TemplateBox px={2}>{renderContactAccInfo()}</TemplateBox>
                  </Grid>
                </Grid>
              </Grid>
            </TemplateBox>
          </Box>
        </div>
      </SplitterLayout>
    </>
  );
}

Details.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  rowData: PropTypes.object.isRequired,
};

export default Details;
